/**
 * Poker hand evaluation — best 5 from N, where N may be FEWER than 5
 * (MULTIPLAYER_GAMES_PLAN.md §2b, all rules Cole's, 2026-08-08):
 *
 *  - Short hands are REAL hands, ranked normally: a pair off three cards
 *    beats a five-card King-high. Straights/flushes need 5 cards, so a short
 *    hand caps at trips on 3 cards and QUADS on exactly 4.
 *  - Kickers, when hands differ in length: compare rank first, then kickers
 *    one at a time descending; the first difference decides; if every
 *    COMPARED kicker ties and one hand runs out, the LONGER hand wins.
 *    Length is the tiebreak, NOT a trump — pair of 8s + K (3 cards) still
 *    beats pair of 8s + 4 + 3 + 2 (5 cards), because the K settles it before
 *    length is consulted. Implemented as "a missing card ranks below any
 *    card" (-1 sentinel in the comparison vector).
 *
 * The evaluator returns a comparison VECTOR [category, ...tiebreaks] so both
 * the showdown and any leaderboard rank with the identical comparator and can
 * never disagree.
 */

import type { PokerCard, PokerRank, PokerSuit } from './pokerDeck';

export enum HandCategory {
  HighCard = 0,
  Pair = 1,
  TwoPair = 2,
  Trips = 3,
  Straight = 4,
  Flush = 5,
  FullHouse = 6,
  Quads = 7,
  StraightFlush = 8,
}

export const HAND_CATEGORY_LABEL: Record<HandCategory, string> = {
  [HandCategory.HighCard]: 'High Card',
  [HandCategory.Pair]: 'Pair',
  [HandCategory.TwoPair]: 'Two Pair',
  [HandCategory.Trips]: 'Trips',
  [HandCategory.Straight]: 'Straight',
  [HandCategory.Flush]: 'Flush',
  [HandCategory.FullHouse]: 'Full House',
  [HandCategory.Quads]: 'Quads',
  [HandCategory.StraightFlush]: 'Straight Flush',
};

export interface EvaluatedHand {
  category: HandCategory;
  /** [category, ...tiebreak ranks descending]. Compare lexicographically with
   *  compareHands; missing entries rank below any real value. */
  vector: number[];
  /** The best five (or fewer) cards, for display, in rank-significant order. */
  cards: PokerCard[];
}

/** Highest straight top given a rank set; 0 = none. Handles the wheel (A-5). */
function straightHigh(ranks: number[]): number {
  const uniq = [...new Set(ranks)].sort((a, b) => b - a);
  if (uniq.includes(14)) uniq.push(1); // ace low
  let run = 1;
  for (let i = 1; i < uniq.length; i++) {
    if (uniq[i] === uniq[i - 1] - 1) {
      run++;
      if (run >= 5) return uniq[i] + 4;
    } else {
      run = 1;
    }
  }
  return 0;
}

function byRankDesc(a: PokerCard, b: PokerCard): number {
  return b.rank - a.rank;
}

/** Lexicographic rank comparison for two already-descending card lists. */
function compareCardRanks(a: PokerCard[], b: PokerCard[]): number {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const rankA = a[i]?.rank ?? -1;
    const rankB = b[i]?.rank ?? -1;
    if (rankA !== rankB) return rankA - rankB;
  }
  return 0;
}

/** Pick display cards for a straight ending at `top` (wheel-aware). */
function straightCards(cards: PokerCard[], top: number, suit?: PokerSuit): PokerCard[] {
  const pool = suit ? cards.filter((c) => c.suit === suit) : cards;
  const out: PokerCard[] = [];
  for (let need = top; need > top - 5; need--) {
    const rank = need === 1 ? 14 : need; // wheel ace
    const found = pool.find((c) => c.rank === rank && !out.includes(c));
    if (found) out.push(found);
  }
  return out;
}

/**
 * Evaluate the best hand available from any number of cards (0 permitted —
 * an empty hand has the empty vector and loses to everything).
 */
export function evaluateHand(cards: PokerCard[]): EvaluatedHand {
  if (cards.length === 0) {
    return { category: HandCategory.HighCard, vector: [HandCategory.HighCard], cards: [] };
  }

  const counts = new Map<number, PokerCard[]>();
  const suits = new Map<PokerSuit, PokerCard[]>();
  for (const c of cards) {
    counts.set(c.rank, [...(counts.get(c.rank) ?? []), c]);
    suits.set(c.suit, [...(suits.get(c.suit) ?? []), c]);
  }

  // Straight flush / flush (5-card shapes — impossible for short hands).
  // A large earned hand can contain 5+ cards in MORE than one suit, so retain
  // the best candidate from every qualifying suit. Keeping only the last suit
  // encountered can discard a royal flush in favour of an unrelated low flush.
  let straightFlushTop = 0;
  let straightFlushSuit: PokerSuit | null = null;
  let bestFlushCards: PokerCard[] = [];
  for (const [suit, cs] of suits) {
    if (cs.length < 5) continue;
    const sfTop = straightHigh(cs.map((c) => c.rank));
    if (sfTop > straightFlushTop) {
      straightFlushTop = sfTop;
      straightFlushSuit = suit;
    }
    const five = cs.slice().sort(byRankDesc).slice(0, 5);
    if (bestFlushCards.length === 0 || compareCardRanks(five, bestFlushCards) > 0) {
      bestFlushCards = five;
    }
  }
  if (straightFlushTop > 0 && straightFlushSuit) {
    return {
      category: HandCategory.StraightFlush,
      vector: [HandCategory.StraightFlush, straightFlushTop],
      cards: straightCards(cards, straightFlushTop, straightFlushSuit),
    };
  }

  // Groups sorted by count desc, then rank desc.
  const groups = [...counts.entries()].sort((a, b) => b[1].length - a[1].length || b[0] - a[0]);
  const [topRank, topCards] = groups[0];
  const topN = topCards.length;
  const second = groups[1] ?? null;
  const tripRank = groups
    .filter(([, grouped]) => grouped.length >= 3)
    .map(([rank]) => rank)
    .sort((a, b) => b - a)[0];
  const fullHousePairRank = groups
    .filter(([rank, grouped]) => rank !== tripRank && grouped.length >= 2)
    .map(([rank]) => rank)
    .sort((a, b) => b - a)[0];

  const kickers = (exclude: number[], take: number): PokerCard[] =>
    cards.filter((c) => !exclude.includes(c.rank)).sort(byRankDesc).slice(0, take);

  if (topN === 4) {
    const k = kickers([topRank], 1);
    return {
      category: HandCategory.Quads,
      vector: [HandCategory.Quads, topRank, ...k.map((c) => c.rank)],
      cards: [...topCards, ...k],
    };
  }
  if (tripRank !== undefined && fullHousePairRank !== undefined) {
    const trips = counts.get(tripRank)!;
    const pair = counts.get(fullHousePairRank)!;
    return {
      category: HandCategory.FullHouse,
      vector: [HandCategory.FullHouse, tripRank, fullHousePairRank],
      cards: [...trips.slice(0, 3), ...pair.slice(0, 2)],
    };
  }
  if (bestFlushCards.length === 5) {
    return {
      category: HandCategory.Flush,
      vector: [HandCategory.Flush, ...bestFlushCards.map((c) => c.rank)],
      cards: bestFlushCards,
    };
  }
  const stTop = straightHigh(cards.map((c) => c.rank));
  if (stTop > 0) {
    return {
      category: HandCategory.Straight,
      vector: [HandCategory.Straight, stTop],
      cards: straightCards(cards, stTop),
    };
  }
  if (topN === 3) {
    const k = kickers([topRank], 2);
    return {
      category: HandCategory.Trips,
      vector: [HandCategory.Trips, topRank, ...k.map((c) => c.rank)],
      cards: [...topCards, ...k],
    };
  }
  if (topN === 2 && second && second[1].length === 2) {
    const k = kickers([topRank, second[0]], 1);
    return {
      category: HandCategory.TwoPair,
      vector: [HandCategory.TwoPair, topRank, second[0], ...k.map((c) => c.rank)],
      cards: [...topCards, ...second[1], ...k],
    };
  }
  if (topN === 2) {
    const k = kickers([topRank], 3);
    return {
      category: HandCategory.Pair,
      vector: [HandCategory.Pair, topRank, ...k.map((c) => c.rank)],
      cards: [...topCards, ...k],
    };
  }
  const high = cards.slice().sort(byRankDesc).slice(0, 5);
  return {
    category: HandCategory.HighCard,
    vector: [HandCategory.HighCard, ...high.map((c) => c.rank)],
    cards: high,
  };
}

/**
 * Lexicographic comparison with the missing-card rule: an absent entry ranks
 * below ANY card (-1), so equal-through-the-shorter-hand resolves to the
 * LONGER hand. Positive = a wins, negative = b wins, 0 = exact tie.
 */
export function compareHands(a: EvaluatedHand, b: EvaluatedHand): number {
  const n = Math.max(a.vector.length, b.vector.length);
  for (let i = 0; i < n; i++) {
    const x = a.vector[i] ?? -1;
    const y = b.vector[i] ?? -1;
    if (x !== y) return x - y;
  }
  return 0;
}

export interface PokerProximityPutt {
  isMake: boolean;
  proximityFt: number | null;
}

/**
 * Average leave across the match for Poker's exact-hand tie-break. Makes are
 * zero feet. A missing/invalid putt cannot beat a complete record, so it
 * produces Infinity; normal local matches always contain all five results.
 */
export function averagePokerProximity(
  putts: ReadonlyArray<PokerProximityPutt | null>
): number {
  if (putts.length === 0) return Number.POSITIVE_INFINITY;
  let total = 0;
  for (const putt of putts) {
    if (putt === null) return Number.POSITIVE_INFINITY;
    const proximity = putt.isMake ? 0 : putt.proximityFt;
    if (proximity === null || !Number.isFinite(proximity)) {
      return Number.POSITIVE_INFINITY;
    }
    total += Math.abs(proximity);
  }
  return total / putts.length;
}

/**
 * Full showdown comparison. Hand rank wins first; only an exact rank/vector
 * tie falls through to the plan's closest-average-proximity rule. Positive
 * means A wins, negative means B wins, zero is an exact tie on both measures.
 */
export function comparePokerShowdown(
  a: EvaluatedHand,
  b: EvaluatedHand,
  puttsA: ReadonlyArray<PokerProximityPutt | null>,
  puttsB: ReadonlyArray<PokerProximityPutt | null>
): number {
  const handComparison = compareHands(a, b);
  if (handComparison !== 0) return handComparison;
  const averageA = averagePokerProximity(puttsA);
  const averageB = averagePokerProximity(puttsB);
  if (averageA === averageB) return 0;
  return averageA < averageB ? 1 : -1;
}

const RANK_WORD: Record<number, string> = {
  2: 'Twos', 3: 'Threes', 4: 'Fours', 5: 'Fives', 6: 'Sixes', 7: 'Sevens',
  8: 'Eights', 9: 'Nines', 10: 'Tens', 11: 'Jacks', 12: 'Queens', 13: 'Kings',
  14: 'Aces',
};
const RANK_WORD_SINGULAR: Record<number, string> = {
  2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five', 6: 'Six', 7: 'Seven',
  8: 'Eight', 9: 'Nine', 10: 'Ten', 11: 'Jack', 12: 'Queen', 13: 'King',
  14: 'Ace',
};

/** "Pair of Eights", "Ace High Straight", "Trip Kings"… */
export function describeHand(hand: EvaluatedHand): string {
  const v = hand.vector;
  switch (hand.category) {
    case HandCategory.HighCard:
      return v.length > 1 ? `${RANK_WORD_SINGULAR[v[1]]} High` : 'No Cards';
    case HandCategory.Pair:
      return `Pair of ${RANK_WORD[v[1]]}`;
    case HandCategory.TwoPair:
      return `Two Pair, ${RANK_WORD[v[1]]} and ${RANK_WORD[v[2]]}`;
    case HandCategory.Trips:
      return `Trip ${RANK_WORD[v[1]]}`;
    case HandCategory.Straight:
      return `${RANK_WORD_SINGULAR[v[1]]} High Straight`;
    case HandCategory.Flush:
      return `${RANK_WORD_SINGULAR[v[1]]} High Flush`;
    case HandCategory.FullHouse:
      return `Full House, ${RANK_WORD[v[1]]} over ${RANK_WORD[v[2]]}`;
    case HandCategory.Quads:
      return `Quad ${RANK_WORD[v[1]]}`;
    case HandCategory.StraightFlush:
      return v[1] === 14
        ? 'Royal Flush'
        : `${RANK_WORD_SINGULAR[v[1]]} High Straight Flush`;
  }
}
